import React, { useState, useEffect } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';

const LoginForm = () => {
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    email: '',
    password: ''
  });

  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Redirecionar para a página de perfil ou dashboard
        navigate('/meus-mapas');
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prevState => ({
      ...prevState,
      [name]: value
    }));
    setError(null); // Limpa erros anteriores
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, userData.email, userData.password);
    } catch (err) {
      setError('Email ou senha inválidos');
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 space-y-6 border border-purple-100 relative overflow-hidden">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="flex items-center gap-2 text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              <span>✨</span>
              <span>Mapas Astrais</span>
            </div>
          </div>
          <p className="text-gray-500">Entre na sua conta</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 text-red-500 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="email">
              E-mail
            </label>
            <div className="relative">
              <input
                id="email"
                name="email"
                type="email"
                value={userData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10"
                placeholder="Seu e-mail"
              />
              <Mail className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="password">
              Senha
            </label>
            <input
              id="password"
              name="password"
              type="password"
              value={userData.password}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
              placeholder="••••••••"
            />
          </div>

          <div className="text-right">
            <a href="/reset-password" className="text-sm text-purple-600 hover:text-purple-700">
              Esqueceu a senha?
            </a>
          </div>

          <Button type="submit" fullWidth>
            Entrar
          </Button>
        </form>

        {/* Sign up link */}
        <p className="text-center text-sm text-gray-500">
          Ainda n&#227;o tem uma conta?{' '}
          <a href="/signup" className="font-medium text-purple-600 hover:text-purple-700">
            Criar conta
          </a>
        </p>

        <p className="text-center text-sm text-gray-500">
          Ao entrar, voc&#234; concorda com os{' '}
          <a href="/terms-of-use" className="font-medium text-purple-600 hover:text-purple-700">
            Termos de Uso
          </a>
        </p>

        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
      </div>
    </div>
  );
};

export default LoginForm;